import { m } from "@plannello/i18n/messages";
import type { MilestoneCreate, MilestonePatch, MilestoneView } from "@plannello/shared";
import type { ProductSchema } from "@/data/types";
import { pushUndo } from "@/data/undo";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { queryClient } from "@/query-client";

/**
 * Milestone writes. Milestones live in each product's schema (["schema", KEY]), which the catalog
 * merges; writes patch that cache at once and refetch it (the live stream does the same for
 * everybody else via `milestone.updated`).
 */

function patchSchema(productKey: string, fn: (list: MilestoneView[]) => MilestoneView[]) {
  queryClient.setQueryData<ProductSchema>(["schema", productKey.toUpperCase()], (old) =>
    old ? { ...old, milestones: fn(old.milestones) } : old,
  );
}

const refresh = (productKey: string) =>
  queryClient.invalidateQueries({ queryKey: ["schema", productKey.toUpperCase()] });

export async function createMilestone(
  productKey: string,
  input: Omit<MilestoneCreate, "productId">,
): Promise<MilestoneView> {
  const saved = await api.post<MilestoneView>(`/api/v1/products/${encodeURIComponent(productKey)}/milestones`, input);
  patchSchema(productKey, (list) => [...list.filter((x) => x.id !== saved.id), saved]);
  void refresh(productKey);
  return saved;
}

export async function updateMilestone(
  productKey: string,
  milestone: MilestoneView,
  patch: MilestonePatch,
  opts: { undoTitle?: string } = {},
): Promise<MilestoneView> {
  const optimistic: MilestoneView = {
    ...milestone,
    ...(patch as Partial<MilestoneView>),
    releasedAt:
      patch.status === "released" ? new Date().toISOString() : patch.status === "open" ? null : milestone.releasedAt,
  };
  patchSchema(productKey, (list) => list.map((x) => (x.id === milestone.id ? optimistic : x)));
  try {
    const saved = await api.patch<MilestoneView>(`/api/v1/milestones/${milestone.id}`, patch);
    patchSchema(productKey, (list) => list.map((x) => (x.id === saved.id ? saved : x)));
    if (opts.undoTitle) {
      const inverse: MilestonePatch = {};
      for (const k of Object.keys(patch) as (keyof MilestonePatch)[]) {
        (inverse as Record<string, unknown>)[k] = milestone[k as keyof MilestoneView] ?? null;
      }
      toast.undo(
        opts.undoTitle,
        pushUndo(() => void updateMilestone(productKey, saved, inverse).catch(() => undefined)),
        m.common_undo(),
      );
    }
    return saved;
  } catch (e) {
    patchSchema(productKey, (list) => list.map((x) => (x.id === milestone.id ? milestone : x)));
    toast.show({ title: m.toast_update_failed(), tone: "danger" });
    throw e;
  } finally {
    void refresh(productKey);
  }
}
